import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { buttonHover } from "../../lib/animations";
import { cn } from "../../lib/cn";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "gradient";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = {
	variant?: ButtonVariant;
	size?: ButtonSize;
	children: ReactNode;
	className?: string;
	disabled?: boolean;
	type?: "button" | "submit" | "reset";
	onClick?: () => void;
};

const variantStyles: Record<ButtonVariant, string> = {
	primary: "bg-pastel-vibrant-blue text-white hover:opacity-90",
	secondary: "bg-pastel-light-gray text-pastel-black hover:bg-pastel-border",
	outline: "border border-pastel-border bg-white text-pastel-black hover:bg-pastel-light-gray",
	ghost: "text-pastel-gray hover:text-pastel-black hover:bg-pastel-light-gray",
	gradient: "btn-gradient text-white",
};

const sizeStyles: Record<ButtonSize, string> = {
	sm: "px-3 py-1.5 text-sm rounded-lg",
	md: "px-4 py-2 text-sm rounded-lg",
	lg: "px-6 py-3 text-base rounded-xl",
};

export function Button({
	variant = "primary",
	size = "md",
	children,
	className,
	disabled = false,
	type = "button",
	onClick,
}: ButtonProps) {
	const hoverProps = disabled
		? {}
		: { whileHover: "hover" satisfies string, whileTap: "tap" satisfies string };

	return (
		<motion.button
			type={type}
			variants={buttonHover}
			initial="rest"
			{...hoverProps}
			disabled={disabled}
			onClick={onClick}
			className={cn(
				"inline-flex items-center justify-center font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40 focus:ring-offset-2",
				variantStyles[variant],
				sizeStyles[size],
				disabled && "opacity-50 cursor-not-allowed",
				className
			)}
		>
			{children}
		</motion.button>
	);
}
